import { useEffect } from "react"
import { subscribe } from "valtio"
import Utils from "../util/Utils"
import { configStore, containerStore, fclChargesStore } from "../state/CommonState"
import { containerTypeweights } from "../StaticData"
import { Card } from "../v3-componets/AgCard"
import { Headspan } from "../v3-componets/AgHeadspan"
import chargedetails from "../assets/images/chargedetails.svg"

// FCL charges are priced per container type: each row carries `${containerType}total`
// (already multiplied by the number of containers) instead of the single LCL `total`.

export const isFCLShipment = (shipmentType) => ('' + (shipmentType || '')).indexOf('FCL') >= 0

export const getContainerList = (quote) =>
    quote?.containerList || quote?.quoteData?.containerList || []

export const fclRowTotal = (row, containerList) => {
    let t = 0
    containerList.forEach((c) => {
        const v = Number(row?.[c.containerType + 'total'])
        if (Number.isFinite(v)) t += v
    })
    return t
}

// Same as Utils.getTotal, for FCL rows
export const getFCLChargesTotal = (list, containerList) => {
    let t = 0
    ;(list || []).forEach((e) => {
        if (e.isIncluded == 'Yes') {
            t += configStore.getExchangeRate(e.currency) * fclRowTotal(e, containerList)
        }
    })
    return t
}

// Marks rows with a non-zero total as included and splits them by account, like the LCL save does
export const splitFCLCharges = (list, containerList) => {
    const chargesList = []
    const shipperchargesList = []
    const consigneechargesList = []
    ;(list || []).forEach((e, i) => {
        e.id = i + 1
        if (fclRowTotal(e, containerList) != 0) {
            e.isIncluded = 'Yes'
            if (e.onaccount == "Shipper's Account") shipperchargesList.push(e)
            else if (e.onaccount == "Consignee's Account") consigneechargesList.push(e)
            else chargesList.push(e)
        }
    })
    return { chargesList, shipperchargesList, consigneechargesList }
}

// Change key for a snapshot of the charge rows that only reads what the totals depend on.
// Reading every field (e.g. JSON.stringify of the snapshot) re-renders the parent on each keystroke,
// which remounts the FCL table's inputs and drops focus.
export const fclTotalsKey = (snapList, containerList) =>
    JSON.stringify((snapList || []).map((e) => [e.id, e.onaccount, e.currency, ...containerList.map((c) => e[c.containerType + 'total'])]))

// Per-container total of one row, same formula as fclChargesStore.calculateChargesTotalsindex
// (a negative rate makes the whole total negative), but without rewriting the entered rates.
const fclContainerTotal = (row, c, emptycontainerweight) => {
    const ct = c.containerType
    const rates = ['perWM', 'perShipment', 'perTon'].map((k) => Number(row[ct + k]) || 0)
    const negative = rates.some((v) => v < 0)
    const [perwm, pership, perton] = rates.map(Math.abs)
    const num = Number(c.numContainers) || 0
    let total = perwm * num + pership
    if (emptycontainerweight == 'yes') {
        const wpc = Number(c.wtPerContainer) || 0
        const ecw = containerTypeweights[ct]?.KG || 0
        total += (num * (wpc + ecw) * perton) / 1000
    }
    total = Utils.R(total)
    return negative ? -total : total
}

// Keeps the revised FCL charge totals up to date while the user types (no calculator click needed)
export const useFCLAutoTotals = (enabled, getEmptyContainerWeight) => {
    useEffect(() => {
        if (!enabled) return
        let timer
        const recalc = () => {
            clearTimeout(timer)
            timer = setTimeout(() => {
                const ecw = getEmptyContainerWeight ? getEmptyContainerWeight() : undefined
                fclChargesStore.list.forEach((row) => {
                    containerStore.list.forEach((c) => {
                        const t = fclContainerTotal(row, c, ecw)
                        if (row[c.containerType + 'total'] !== t) row[c.containerType + 'total'] = t
                    })
                })
            }, 300)
        }
        recalc()
        const unsubCharges = subscribe(fclChargesStore, recalc)
        const unsubContainers = subscribe(containerStore, recalc)
        return () => { clearTimeout(timer); unsubCharges(); unsubContainers() }
    }, [enabled])
}

export const hasMissingFCLDetails = (list, containerList) =>
    (list || []).some((e) => fclRowTotal(e, containerList) != 0 && (!e.chargeDescription || !e.currency))

export function FCLChargesView({ list, containerList, heading }) {
    const rows = list || []
    const containers = containerList || []
    const accountTotal = (prefix) => {
        const totals = {}
        containers.forEach((c) => (totals[c.containerType] = 0))
        rows.forEach((e) => {
            const account = e.onaccount || 'On Your Account'
            if (!account.startsWith(prefix)) return
            let erate = configStore.getExchangeRateinr(e)
            if (!erate || erate === 0) erate = 1.0
            containers.forEach((c) => {
                totals[c.containerType] += (Number(e[c.containerType + 'total']) || 0) * erate
            })
        })
        return totals
    }
    const accounts = [['On Your Account', 'On'], ["On Consignee's Account", 'Cons'], ["On Shipper's Account", 'Ship']]
        .filter(([, prefix]) => prefix == 'On' || rows.some((e) => (e.onaccount || '').startsWith(prefix)))
    const th = { color: process.env.REACT_APP_ENTERPRISE_COLOUR, padding: '10px' }
    const td = { color: '#555555', padding: '10px' }
    return (
        <Card>
            <span><img src={chargedetails} /></span>
            <Headspan color="#00000" fontSize="14px" fontWeight="900" data={' ' + (heading || 'Charge Details')} />
            <div className="table-container">
                <table style={{ border: 'none', width: '100%' }}>
                    <thead>
                        <tr>
                            <th colSpan={3}></th>
                            {containers.map((c) => (
                                <td key={c.containerType} colSpan={4} style={{ backgroundColor: '#4BA7DD', color: '#fff', fontWeight: '900', padding: '10px', textAlign: 'center' }}>
                                    {c.containerType} x {c.numContainers}
                                </td>
                            ))}
                        </tr>
                        <tr style={{ fontWeight: '900', border: 'solid 0.5px #D4D4D4' }}>
                            <th style={th}>Charge Description</th>
                            <th style={th}>On Account</th>
                            <th style={th}>Currency</th>
                            {containers.map((c) => (
                                <>
                                    <th style={{ ...th, textAlign: 'center' }}>Per Container</th>
                                    <th style={{ ...th, textAlign: 'center' }}>Per BL</th>
                                    <th style={{ ...th, textAlign: 'center' }}>Per Ton</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Total</th>
                                </>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((e, i) => (
                            <tr key={i} style={{ border: 'solid 0.5px #D4D4D4', backgroundColor: i % 2 == 1 ? '#F3F3F3' : 'none' }}>
                                <td style={td}>{e.chargeDescription}</td>
                                <td style={td}>{e.onaccount || 'On Your Account'}</td>
                                <td style={td}>{e.currency}</td>
                                {containers.map((c) => {
                                    const ct = c.containerType
                                    const cell = (k) => (Number(e[ct + k]) ? Utils.N(e[ct + k], e.currency) : '')
                                    return (
                                        <>
                                            <td style={{ ...td, textAlign: 'center' }}>{cell('perWM')}</td>
                                            <td style={{ ...td, textAlign: 'center' }}>{cell('perShipment')}</td>
                                            <td style={{ ...td, textAlign: 'center' }}>{cell('perTon')}</td>
                                            <td style={{ ...td, textAlign: 'right' }}>{cell('total')}</td>
                                        </>
                                    )
                                })}
                            </tr>
                        ))}
                        {accounts.map(([label, prefix]) => {
                            const totals = accountTotal(prefix)
                            return (
                                <tr key={label} style={{ border: 'solid 0.5px #D4D4D4' }}>
                                    <td colSpan={3} style={{ ...td, textAlign: 'right', fontWeight: '900' }}>{label}</td>
                                    {containers.map((c) => (
                                        <>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            <td style={{ textAlign: 'right' }}><strong>{Utils.N(totals[c.containerType])}</strong></td>
                                        </>
                                    ))}
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </Card>
    )
}
